import React, { useState, useEffect } from 'react';
import { Menu, X, MessageCircle, ArrowRight, Phone, Mail, MapPin, Presentation, Coffee, Users, Building2, Trees, Wifi } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { contactInfo, tagungsPauschale, faqData, mockFormSubmission } from '../data/mockData';

const BusinessLandingFinal = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showCookieSettings, setShowCookieSettings] = useState(false);
  const [showCookieBanner, setShowCookieBanner] = useState(true);
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false
  });
  
  const [formData, setFormData] = useState({
    company: '',
    contactPerson: '',
    email: '',
    phone: '',
    eventType: '',
    participants: '',
    dates: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (cookieConsent) {
      setShowCookieBanner(false);
      setCookiePreferences(JSON.parse(cookieConsent));
    }
  }, []);

  const handleCookieAcceptAll = () => {
    const preferences = { essential: true, analytics: true, marketing: true };
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setCookiePreferences(preferences);
    setShowCookieBanner(false);
    toast.success('Cookie-Einstellungen gespeichert');
  };

  const handleCookieEssentialOnly = () => {
    const preferences = { essential: true, analytics: false, marketing: false };
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    setCookiePreferences(preferences);
    setShowCookieBanner(false);
    toast.success('Nur essenzielle Cookies aktiviert');
  };

  const handleCookieSettings = () => {
    setShowCookieSettings(true);
  };

  const saveCookiePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(cookiePreferences));
    setShowCookieBanner(false);
    setShowCookieSettings(false);
    toast.success('Cookie-Einstellungen gespeichert');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await mockFormSubmission(formData);
      if (result.success) {
        toast.success(result.message);
        setFormData({
          company: '',
          contactPerson: '',
          email: '',
          phone: '',
          eventType: '',
          participants: '',
          dates: '',
          message: ''
        });
      }
    } catch (error) {
      toast.error("Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    const text = encodeURIComponent("Hallo, wir planen ein Firmen-Offsite und interessieren uns für Ihre Tagungsräume.");
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${text}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header mit Original-Logo */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-[#C8A96E]/20">
        <div className="container mx-auto px-6 h-20 flex items-center justify-between">
          <a href="/" className="flex items-center">
            <img 
              src="/waldhotel-logo.svg"
              alt="Waldhotel Bad Essen"
              className="h-16 w-auto"
            />
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            <a href="#business" className="text-sm text-[#253541] hover:text-[#28863E] transition-colors tracking-wide">Business-Übernachtung</a>
            <a href="#tagungen" className="text-sm text-[#253541] hover:text-[#28863E] transition-colors tracking-wide">Tagungen</a>
            <a href="#anfrage" className="text-sm text-[#253541] hover:text-[#28863E] transition-colors tracking-wide">Anfrage</a>
            <a href="#faq" className="text-sm text-[#253541] hover:text-[#28863E] transition-colors tracking-wide">FAQ</a>
            <Button onClick={openWhatsApp} className="bg-[#28863E] hover:bg-[#1f6d32] text-white rounded-[3.5px]">
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp
            </Button>
          </div>
          
          <button 
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2"
            aria-label="Menu"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-[#2E4A32]" strokeWidth={2} />
            ) : (
              <Menu className="w-6 h-6 text-[#2E4A32]" strokeWidth={2} />
            )}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-white pt-20 md:hidden">
          <nav className="container mx-auto px-6 py-8">
            <ul className="space-y-6">
              <li><a href="#business" onClick={() => setIsMenuOpen(false)} className="font-heading text-2xl text-[#253541] hover:text-[#28863E] transition-colors">Business-Übernachtung</a></li>
              <li><a href="#tagungen" onClick={() => setIsMenuOpen(false)} className="font-heading text-2xl text-[#253541] hover:text-[#28863E] transition-colors">Tagungen</a></li>
              <li><a href="#anfrage" onClick={() => setIsMenuOpen(false)} className="font-heading text-2xl text-[#253541] hover:text-[#28863E] transition-colors">Anfrage</a></li>
              <li><a href="#faq" onClick={() => setIsMenuOpen(false)} className="font-heading text-2xl text-[#253541] hover:text-[#28863E] transition-colors">FAQ</a></li>
            </ul>
          </nav>
        </div>
      )}

      {/* Hero Section mit echtem Tagungsraum-Foto */}
      <section className="relative h-screen overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_closed-house/artifacts/0kqh6q13_566407e0-8ef7-482f-9319-6996309b2627.jpeg"
            alt="Tagungsraum im Waldhotel Bad Essen"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#253541]/70 via-[#253541]/30 to-transparent"></div>
        
        <div className="relative h-full flex flex-col justify-end pb-20 md:pb-32 container mx-auto px-6">
          <div className="max-w-2xl">
            <p className="subtitle-caps text-[#C8A96E] mb-4">
              Seit 1906. Ankommen. Aufatmen. Aufleben.
            </p>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-[56px] text-white font-light tracking-wide leading-[1.1] mb-6">
              Ihr Business-Hub am Wiehengebirge
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 leading-relaxed">
              Ungestörtes Arbeiten. Fokussiertes Networking. Exklusive Atmosphäre.
            </p>
            <div className="gold-line mb-8"></div>
            <a 
              href="#anfrage"
              className="inline-flex items-center gap-2 bg-[#28863E] text-white px-7 py-3.5 text-sm tracking-wide rounded-[3.5px] hover:bg-[#1f6d32] transition-all duration-300 group"
            >
              Jetzt anfragen
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </div>
        </div>
      </section>

      {/* Split Navigation Cards */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <a href="#business" className="group">
              <div className="bg-[#F7F5F0] rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300">
                <Building2 className="w-12 h-12 text-[#28863E] mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-heading text-[28px] text-[#253541] font-light mb-4">
                  Business-Übernachtung
                </h3>
                <p className="text-base text-[#808080] leading-relaxed mb-6">
                  Für Monteure, Geschäftsreisende und Projektteams. 20 Zimmer, Waldsauna, kostenfreies WLAN.
                </p>
                <span className="inline-flex items-center gap-2 text-sm text-[#28863E] font-medium group-hover:gap-3 transition-all">
                  Mehr erfahren
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </a>

            <a href="#tagungen" className="group">
              <div className="bg-[#F7F5F0] rounded-2xl p-8 h-full hover:shadow-xl transition-all duration-300">
                <Users className="w-12 h-12 text-[#28863E] mb-6 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-heading text-[28px] text-[#253541] font-light mb-4">
                  Tagungen & Events
                </h3>
                <p className="text-base text-[#808080] leading-relaxed mb-6">
                  Fokussierte Workshops, Firmen-Offsites und Teamevents. Separierter Tagungsbereich mit Exklusivität.
                </p>
                <span className="inline-flex items-center gap-2 text-sm text-[#28863E] font-medium group-hover:gap-3 transition-all">
                  Mehr erfahren
                  <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Tagungen Section mit Grid Layout */}
      <section id="tagungen" className="section-padding bg-[#F7F5F0]">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-5">
              <img 
                src="https://files.manuscdn.com/user_upload_by_module/session_file/310519663365901872/LeevHzFncsJiItfb.jpg"
                alt="Waldhotel Bad Essen Außenansicht"
                className="w-full h-[400px] lg:h-[520px] object-cover rounded-lg"
              />
            </div>
            
            <div className="lg:col-span-7">
              <p className="subtitle-caps text-[#253541] mb-4">Tagungen & Events</p>
              <h2 className="font-heading text-[28px] md:text-[34px] text-[#253541] font-light leading-tight mb-6">
                Fokus und Austausch in ungestörter Atmosphäre
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="text-base leading-[1.75] text-[#253541]/80 mb-8">
                Unser Setup trennt Arbeit und Regeneration perfekt. Der Tagungsbereich liegt separiert vom restlichen Hotelbetrieb. Keine Laufkundschaft. Keine Störungen.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Presentation className="w-5 h-5 text-[#28863E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#253541] mb-1">Tageslicht-Räume</h4>
                    <p className="text-sm text-[#808080]">Beamer, Highspeed-WLAN, Moderationsmaterial</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Coffee className="w-5 h-5 text-[#28863E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#253541] mb-1">Getränke-Flatrate</h4>
                    <p className="text-sm text-[#808080]">Kaffee, Tee, Wasser, Cola & Cola Zero inklusive</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Trees className="w-5 h-5 text-[#28863E]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#253541] mb-1">Networking-Zonen</h4>
                    <p className="text-sm text-[#808080]">Garten, Terrasse und ruhige Lounge-Bereiche</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Networking Zones */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <p className="subtitle-caps text-[#253541] mb-4">Rückzugsorte</p>
              <h2 className="font-heading text-[28px] md:text-[34px] text-[#253541] font-light leading-tight mb-6">
                Wald, Wandern & Teambuilding
              </h2>
              <div className="gold-line mb-8"></div>
              <p className="text-base leading-[1.75] text-[#253541]/80 mb-8">
                Die besten Ideen entstehen nicht am Schreibtisch. Das Wiehengebirge direkt vor der Tür lädt zu Wanderungen und Outdoor-Teambuilding ein. Nutzen Sie unseren weitläufigen Garten, die Terrasse und unsere ruhigen Lounge-Ecken für 1-on-1-Gespräche und informelles Netzwerken.
              </p>
              <ul className="space-y-3 text-[#253541]/80">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C8A96E] rounded-full"></span>
                  Wanderwege im Wiehengebirge für Teambuilding
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C8A96E] rounded-full"></span>
                  Weitläufiger Garten mit Sitzgruppen
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C8A96E] rounded-full"></span>
                  Gemütliche Lounge-Bereiche
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C8A96E] rounded-full"></span>
                  Ruhige Breakout-Räume
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 bg-[#C8A96E] rounded-full"></span>
                  Terrasse mit Blick ins Grüne
                </li>
              </ul>
            </div>
            
            <div className="lg:col-span-5 order-1 lg:order-2">
              <img 
                src="https://images.unsplash.com/photo-1669635455986-95af2fb7e755?q=85"
                alt="Wandern und Teambuilding im Wiehengebirge"
                className="w-full h-[400px] lg:h-[520px] object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Verpflegung & Closed House mit Bildern */}
      <section className="section-padding bg-[#F7F5F0]">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              {/* Verpflegung mit Frühstücksbild */}
              <div>
                <div className="rounded-lg overflow-hidden mb-8">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_closed-house/artifacts/sfdd6gkc_42f434d9-fed7-4062-82c2-c4fc301ecca8.jpeg"
                    alt="Frühstück im Waldhotel"
                    className="w-full h-[300px] object-cover"
                  />
                </div>
                <p className="subtitle-caps text-[#253541] mb-4">Verpflegung</p>
                <h3 className="font-heading text-[28px] text-[#253541] font-light mb-6">
                  Tagungspauschale ab {tagungsPauschale.priceFrom} €
                </h3>
                <div className="gold-line mb-6"></div>
                <div className="space-y-3">
                  {tagungsPauschale.includes.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 bg-[#28863E] rounded-full mt-2"></span>
                      <span className="text-sm text-[#253541]/80">{item}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-[#808080] mt-6">
                  Modular zubuchbar: {tagungsPauschale.optional.join(', ')}
                </p>
              </div>

              {/* Networking-Pause mit Business-Bild */}
              <div>
                <div className="rounded-lg overflow-hidden mb-8">
                  <img 
                    src="https://images.unsplash.com/photo-1714974528767-cbe4f78363d0?q=85"
                    alt="Business Networking Pause"
                    className="w-full h-[300px] object-cover"
                  />
                </div>
                <p className="subtitle-caps text-[#253541] mb-4">Exklusivität</p>
                <h3 className="font-heading text-[28px] text-[#253541] font-light mb-6">
                  Closed House Buchung
                </h3>
                <div className="gold-line mb-6"></div>
                <p className="text-base leading-[1.75] text-[#253541]/80 mb-6">
                  Ab ca. 20 Zimmern buchen Sie uns als geschlossene Gesellschaft. Das gesamte Haus wird zu Ihrem privaten Business-Hub.
                </p>
                <p className="text-sm text-[#808080]">
                  Keine anderen Gäste. Keine Ablenkung. Nur Ihr Team und die perfekte Umgebung für produktive Arbeit und wertvollen Austausch.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Business Übernachtung mit Bildern */}
      <section id="business" className="section-padding bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="subtitle-caps text-[#253541] mb-4">Business-Übernachtung</p>
            <h2 className="font-heading text-[28px] md:text-[34px] text-[#253541] font-light leading-tight mb-6">
              Für Monteure und Geschäftsreisende
            </h2>
            <div className="gold-line mx-auto mb-8"></div>
          </div>

          {/* Bilder-Grid mit Original Waldhotel Bildern */}
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            <div className="rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_closed-house/artifacts/joiq122y_3c1b4185-4bdd-4d8e-94f7-8d12110f5434.jpeg"
                alt="Zimmer im Waldhotel"
                className="w-full h-[280px] object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_closed-house/artifacts/f1c65k7b_f0cc5603-6ba7-47aa-81db-31317ea0c62f.jpeg"
                alt="Badezimmer im Waldhotel"
                className="w-full h-[280px] object-cover"
              />
            </div>
            <div className="rounded-lg overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_closed-house/artifacts/9655fx1t_bb530cb2-1b9d-4099-bcb5-491099f041c4.jpeg"
                alt="Arbeitsbereich im Waldhotel"
                className="w-full h-[280px] object-cover"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F7F5F0] flex items-center justify-center mx-auto mb-6">
                <Building2 className="w-7 h-7 text-[#28863E]" strokeWidth={1.5} />
              </div>
              <h4 className="font-heading text-lg text-[#253541] mb-3">20 Zimmer</h4>
              <p className="text-sm text-[#808080]">Boxspringbetten, Blick ins Grüne, kostenfreies WLAN</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F7F5F0] flex items-center justify-center mx-auto mb-6">
                <Trees className="w-7 h-7 text-[#28863E]" strokeWidth={1.5} />
              </div>
              <h4 className="font-heading text-lg text-[#253541] mb-3">Waldsauna</h4>
              <p className="text-sm text-[#808080]">500 m² Saunalandschaft direkt am Hotel</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-[#F7F5F0] flex items-center justify-center mx-auto mb-6">
                <Wifi className="w-7 h-7 text-[#28863E]" strokeWidth={1.5} />
              </div>
              <h4 className="font-heading text-lg text-[#253541] mb-3">Komfort</h4>
              <p className="text-sm text-[#808080]">Kostenfreie Parkplätze direkt am Haus</p>
            </div>
          </div>
        </div>
      </section>

      {/* Anfrage Section */}
      <section id="anfrage" className="section-padding bg-[#F7F5F0]">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-16">
              <p className="subtitle-caps text-[#253541] mb-4">Anfrage</p>
              <h2 className="font-heading text-[28px] md:text-[34px] text-[#253541] font-light leading-tight mb-6">
                Jetzt Anfrage starten
              </h2>
              <div className="gold-line mx-auto mb-6"></div>
              <p className="text-base text-[#253541]/80">
                Wir melden uns innerhalb von 24 Stunden
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <div className="w-16 h-16 rounded-full bg-[#28863E]/10 flex items-center justify-center mb-6">
                  <MessageCircle className="w-7 h-7 text-[#28863E]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-[24px] text-[#253541] font-light mb-4">
                  Direkter WhatsApp-Kontakt
                </h3>
                <p className="text-sm text-[#808080] mb-6 leading-relaxed">
                  Schnell und unkompliziert: Kontaktieren Sie unser Team direkt per WhatsApp für eine erste Termin-Absprache.
                </p>
                <Button
                  onClick={openWhatsApp}
                  className="w-full bg-[#28863E] hover:bg-[#1f6d32] text-white rounded-[3.5px]"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp-Chat starten
                </Button>
                <p className="text-xs text-[#808080] text-center mt-4">
                  {contactInfo.phone}
                </p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-sm">
                <h3 className="font-heading text-[24px] text-[#253541] font-light mb-6">
                  Anfrage-Formular
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="company" className="text-sm text-[#253541] mb-1">Firma *</Label>
                    <Input 
                      id="company" 
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required 
                      className="border-[#253541]/20 rounded-[3.5px] focus:border-[#28863E]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson" className="text-sm text-[#253541] mb-1">Ansprechpartner *</Label>
                    <Input 
                      id="contactPerson" 
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      required 
                      className="border-[#253541]/20 rounded-[3.5px] focus:border-[#28863E]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm text-[#253541] mb-1">E-Mail *</Label>
                    <Input 
                      id="email" 
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required 
                      className="border-[#253541]/20 rounded-[3.5px] focus:border-[#28863E]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm text-[#253541] mb-1">Nachricht</Label>
                    <Textarea 
                      id="message" 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={3}
                      className="border-[#253541]/20 rounded-[3.5px] focus:border-[#28863E]"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-[#28863E] hover:bg-[#1f6d32] text-white rounded-[3.5px]"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Wird gesendet..." : "Anfrage absenden"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="section-padding bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-16">
              <p className="subtitle-caps text-[#253541] mb-4">FAQ</p>
              <h2 className="font-heading text-[28px] md:text-[34px] text-[#253541] font-light leading-tight mb-6">
                Häufig gestellte Fragen
              </h2>
              <div className="gold-line mx-auto"></div>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqData.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="bg-[#F7F5F0] rounded-2xl border-none px-6"
                >
                  <AccordionTrigger className="text-left font-heading text-base text-[#253541] hover:text-[#28863E] py-5 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-[#808080] leading-relaxed pb-5">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#253541] text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
            <div>
              <img 
                src="/waldhotel-logo.svg"
                alt="Waldhotel Bad Essen"
                className="h-16 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-sm text-white/60 leading-relaxed">
                Seit 1906 am Fuße des Wiehengebirges
              </p>
            </div>
            
            <div>
              <h4 className="font-heading text-base mb-4 text-[#C8A96E]">Kontakt</h4>
              <ul className="space-y-3 text-sm text-white/80">
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4 flex-shrink-0" />
                  {contactInfo.phone}
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4 flex-shrink-0" />
                  {contactInfo.email}
                </li>
                <li className="flex items-start gap-3 mt-4">
                  <MapPin className="w-4 h-4 mt-1 flex-shrink-0" />
                  <span>
                    {contactInfo.address.street}<br />
                    {contactInfo.address.zip} {contactInfo.address.city}
                  </span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-heading text-base mb-4 text-[#C8A96E]">Rechtliches</h4>
              <ul className="space-y-3 text-sm">
                <li>
                  <a href="https://waldhotel-badessen.de/impressum" className="text-white/60 hover:text-white transition-colors">
                    Impressum
                  </a>
                </li>
                <li>
                  <a href="https://waldhotel-badessen.de/datenschutz" className="text-white/60 hover:text-white transition-colors">
                    Datenschutz
                  </a>
                </li>
                <li>
                  <button onClick={() => setShowCookieBanner(true)} className="text-white/60 hover:text-white transition-colors">
                    Cookie-Einstellungen
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8">
            <p className="text-xs text-white/40 text-center">
              Alle Preise zzgl. MwSt. · Waldhotel Bad Essen UG (haftungsbeschränkt) · HRB 222377
            </p>
          </div>
        </div>
      </footer>

      {/* DSGVO Cookie Banner */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-[60] bg-[#253541] text-white border-t border-[#C8A96E]/30 animate-in slide-in-from-bottom duration-500">
          <div className="container mx-auto px-6 py-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
              <div className="flex-1">
                <h3 className="font-heading text-lg mb-2">Cookie-Einstellungen</h3>
                <p className="text-sm text-white/70 leading-relaxed">
                  Wir verwenden essenzielle Cookies für die Funktionalität unserer Website. Mit Ihrer Zustimmung verwenden wir auch Analyse-Cookies zur Verbesserung unseres Angebots. 
                  <a href="https://waldhotel-badessen.de/datenschutz" target="_blank" rel="noopener noreferrer" className="text-[#C8A96E] underline underline-offset-2 ml-1">
                    Mehr erfahren
                  </a>
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleCookieAcceptAll}
                  className="bg-[#28863E] hover:bg-[#1f6d32] text-white rounded-[3.5px] text-sm"
                >
                  Alle akzeptieren
                </Button>
                <Button 
                  onClick={handleCookieSettings}
                  variant="outline"
                  className="bg-white/10 hover:bg-white/20 text-white border-white/20 rounded-[3.5px] text-sm"
                >
                  Einstellungen
                </Button>
                <Button 
                  onClick={handleCookieEssentialOnly}
                  variant="outline"
                  className="bg-transparent hover:bg-white/10 text-white border-white/30 rounded-[3.5px] text-sm"
                >
                  Nur essenzielle
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Settings Dialog */}
      <Dialog open={showCookieSettings} onOpenChange={setShowCookieSettings}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-[#253541]">Cookie-Einstellungen</DialogTitle>
            <DialogDescription className="text-[#808080]">
              Verwalten Sie Ihre Cookie-Präferenzen. Essenzielle Cookies sind für die Funktionalität der Website erforderlich.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-[#253541] mb-1">Essenzielle Cookies</h4>
                <p className="text-sm text-[#808080]">
                  Notwendig für die Grundfunktionen der Website (immer aktiv)
                </p>
              </div>
              <div className="ml-4">
                <input 
                  type="checkbox" 
                  checked={true} 
                  disabled 
                  className="w-5 h-5"
                />
              </div>
            </div>

            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-[#253541] mb-1">Analyse-Cookies</h4>
                <p className="text-sm text-[#808080]">
                  Helfen uns, die Nutzung unserer Website zu verstehen und zu verbessern
                </p>
              </div>
              <div className="ml-4">
                <input 
                  type="checkbox" 
                  checked={cookiePreferences.analytics}
                  onChange={(e) => setCookiePreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                  className="w-5 h-5 accent-[#28863E]"
                />
              </div>
            </div>

            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="font-medium text-[#253541] mb-1">Marketing-Cookies</h4>
                <p className="text-sm text-[#808080]">
                  Ermöglichen personalisierte Werbung und Marketing-Kampagnen
                </p>
              </div>
              <div className="ml-4">
                <input 
                  type="checkbox" 
                  checked={cookiePreferences.marketing}
                  onChange={(e) => setCookiePreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                  className="w-5 h-5 accent-[#28863E]"
                />
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button 
              onClick={saveCookiePreferences}
              className="flex-1 bg-[#28863E] hover:bg-[#1f6d32] text-white rounded-[3.5px]"
            >
              Einstellungen speichern
            </Button>
            <Button 
              onClick={() => setShowCookieSettings(false)}
              variant="outline"
              className="rounded-[3.5px]"
            >
              Abbrechen
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusinessLandingFinal;
